import Big from 'big.js';

// Make the big.js configuration explicit rather than inherited by accident.
// DP bounds division results (divisions are exact up to 20 dp, then
// truncate); RM = half-up is the rounding mode used for money everywhere.
Big.DP = 20;
Big.RM = Big.roundHalfUp;

export { Big };

export type BigSource = Big | number | string;

/**
 * Default CALCULATION money precision (decimal places). Intermediate money
 * VALUES — valuations, line extensions, ledger changes — are computed and
 * kept at 2 dp; this is what a merchant currency's `decimal_places: 2`
 * refers to. Per-unit RATES (stored unit costs) keep the higher
 * UNIT_COST_DECIMALS precision, and FINAL amounts (see roundFinalMoney)
 * are always whole numbers.
 */
export const DEFAULT_MONEY_DECIMALS = 2;

/**
 * Precision of stored per-unit COST rates (weighted-average costs, derived
 * fraction costs). A rate's rounding error is amplified by the quantity it
 * is later multiplied by: at 2 dp a receive into Q units can shift the
 * recomputed stock value by up to 0.005 × Q (hundreds of MT at large
 * stocks); at 6 dp the same bound is 0.0000005 × Q — sub-cent even at a
 * million units, so Start + Changes ≈ End holds to the displayed metical.
 * Costs are rates, not payable amounts, so the whole-unit final-money rule
 * does not apply to them.
 */
export const UNIT_COST_DECIMALS = 6;

/**
 * Precision of FINAL amounts: money in the markets this system serves is
 * settled in whole currency units (no coins/decimals in practice), so
 * every final amount — a payment, a debt balance, a receipt total, a cash
 * correction — is a whole number.
 */
export const FINAL_MONEY_DECIMALS = 0;

/**
 * Money precision is only meaningful between 0 and UNIT_COST_DECIMALS
 * decimal places (unit-cost rates are the finest-grained money stored).
 */
function clampMoneyDecimals(decimals: number): number {
  if (typeof decimals !== 'number' || !isFinite(decimals)) {
    return DEFAULT_MONEY_DECIMALS;
  }
  return Math.min(UNIT_COST_DECIMALS, Math.max(0, Math.trunc(decimals)));
}

/**
 * Rounds a monetary value (half-up) and returns a number. Defaults to the
 * internal 2-dp CALCULATION precision; final amounts use roundFinalMoney
 * instead.
 *
 * This is the single, canonical rounding for money at the persistence
 * boundary: do all arithmetic in Big (or on already-rounded stored values),
 * then round exactly once — with this helper — before a money amount is
 * written to the database or returned in an API payload. Never round money
 * with `Math.round`/`.round(0)` ad hoc and never persist a raw float
 * product/quotient: both introduce the drift this helper exists to prevent.
 */
export function roundMoney(
  value: BigSource,
  decimals: number = DEFAULT_MONEY_DECIMALS,
): number {
  return new Big(value).round(clampMoneyDecimals(decimals)).toNumber();
}

/**
 * CALCULATION decimal places of a merchant's currency, safely resolved.
 * `decimal_places` describes how many decimals intermediate calculations
 * carry (2 in practice), NOT settlement precision — final amounts are
 * always whole (see roundFinalMoney). Falls back to 2; clamped to 0–6.
 */
export function currencyDecimals(
  currency?: { decimal_places?: number | null } | null,
): number {
  const dp = currency?.decimal_places;
  return typeof dp === 'number' && isFinite(dp)
    ? clampMoneyDecimals(dp)
    : DEFAULT_MONEY_DECIMALS;
}

/**
 * Rounds a FINAL money amount to a whole number (half-up).
 *
 * A final amount is one that changes hands or is owed — a payment, a debt
 * principal/installment/outstanding balance, a receipt total, a cash
 * correction — persisted or returned with no further arithmetic before a
 * person acts on it. Money here is settled in whole currency units, so a
 * final amount must be a whole number: otherwise the displayed value and
 * the stored value diverge and a balance can never be settled (a debt of
 * 500.14 shown as "500" is unpayable). Internal calculation values (unit
 * costs, valuations, line extensions) must NOT use this — they keep the
 * 2-dp precision via roundMoney.
 */
export function roundFinalMoney(value: BigSource): number {
  return roundMoney(value, FINAL_MONEY_DECIMALS);
}

/**
 * Rounds a per-unit COST rate (half-up) to UNIT_COST_DECIMALS.
 *
 * Use this — not roundMoney — wherever a unit cost is persisted: the
 * weighted-average cost written after a purchase-order receive, and the
 * fraction costs derived from a parent's cost. Money VALUES computed from
 * these rates (stock × cost, receive totals) still round with roundMoney,
 * and FINAL amounts with roundFinalMoney.
 */
export function roundUnitCost(value: BigSource): number {
  return roundMoney(value, UNIT_COST_DECIMALS);
}

/**
 * Sums an array of Big-compatible values with full precision.
 */
export function bigSum(values: BigSource[]): Big {
  let result = new Big(0);
  for (const v of values) {
    result = result.plus(v);
  }
  return result;
}

/**
 * Returns the maximum of two Big-compatible values.
 */
export function bigMax(a: BigSource, b: BigSource): Big {
  const ba = new Big(a);
  const bb = new Big(b);
  return ba.gte(bb) ? ba : bb;
}

/**
 * Returns the minimum of two Big-compatible values.
 */
export function bigMin(a: BigSource, b: BigSource): Big {
  const ba = new Big(a);
  const bb = new Big(b);
  return ba.lte(bb) ? ba : bb;
}

/**
 * Precision of a stored STOCK quantity (parent units). Stock is kept on
 * the parent; a child unit is a `fraction` of it (1/24 for a case of 24,
 * 1/2000 for a bag of 2000). A sale subtracts `units × fraction`, which is
 * a repeating decimal for most pack sizes, so the cumulative stock is
 * rounded here, once, after the arithmetic: fine enough that even a
 * fraction of 1/100000 keeps every unit (error ≤ 5e-13 per write), and
 * coarse enough to drop the binary noise of `3 × 0.05 = 0.15000000000000002`.
 * Never round the change of one sale at a fixed precision: at 6 dp a case
 * of 24 lost its last unit (23 × 0.041667 leaves 0.041659 < 1/24) and a bag
 * of 3000 gained three.
 */
export const STOCK_DECIMALS = 12;

/**
 * Precision of a stock quantity expressed in UNITS of a product (stock ÷
 * fraction). 3 dp absorbs the float drift of hundreds of sales (about
 * 1e-13 of a unit each) without ever inventing a unit: 0.9995 units would
 * need a drift 5e9 times larger. Relative to the product's own fraction,
 * so it holds for a case of 6 and a bag of 2000 alike.
 */
export const STOCK_UNIT_DECIMALS = 3;

/** Rounds a stock quantity (parent units) for storage, see STOCK_DECIMALS. */
export function roundStock(value: BigSource): number {
  return new Big(value).round(STOCK_DECIMALS).toNumber();
}

/**
 * The whole and fractional units of a product that `inventory` (parent
 * units) holds: `inventory ÷ fraction`, rounded to STOCK_UNIT_DECIMALS.
 * The one way to turn stock into units; a raw `inventory >= fraction`
 * rejects the last unit of a case once the float tail falls below it.
 */
export function stockUnits(inventory: BigSource, fraction: BigSource): number {
  const f = new Big(fraction || 1);
  const divisor = f.lte(0) ? new Big(1) : f;
  return new Big(inventory || 0)
    .div(divisor)
    .round(STOCK_UNIT_DECIMALS)
    .toNumber();
}

/** True when `inventory` holds at least `units` units of the product. */
export function hasStockForUnits(
  inventory: BigSource,
  fraction: BigSource,
  units: BigSource = 1,
): boolean {
  return new Big(stockUnits(inventory, fraction)).gte(units);
}
